import { ScrollArea } from "@atlas/core";
import { FilterBuilder, useTranslation, useZodSearchParams } from "@atlas/sdk";
import { useSearchHubPackageDeployments, useUserInfo } from "@query";
import { APP_BASE_PATH } from "@shared";
import { useMemo } from "react";
import { z } from "zod";
import { CheckoutCatalogs } from "./CheckoutCatalogs";
import { DeploymentSummaryCard } from "./DeploymentSummaryCard";

const deploymentSearchParamsSchema = z.object({
  deploymentId: z.coerce.number().optional(),
});

export function DeploymentDetails() {
  const { t } = useTranslation();
  const { deploymentId } = useZodSearchParams(deploymentSearchParamsSchema);
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const companyId = userInfo?.companyId ?? 0;

  const filter = useMemo(() => {
    return new FilterBuilder()
      .equals("companyId", companyId)
      .equals("deploymentId", deploymentId ?? 0)
      .build();
  }, [companyId, deploymentId]);

  const { data: searchData, isLoading: deploymentLoading } =
    useSearchHubPackageDeployments(
      { filter },
      { enabled: companyId > 0 && (deploymentId ?? 0) > 0 },
    );

  const deployment = useMemo(
    () => searchData?.pages?.[0]?.items?.[0] ?? null,
    [searchData],
  );

  const installedCatalogs = useMemo(
    () => deployment?.installedCatalogs ?? [],
    [deployment],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {!deploymentLoading && installedCatalogs.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8 text-muted-foreground">
          {t("hub.checkout.empty_deployment", {
            defaultValue: "No catalogs found for this deployment.",
          })}
        </div>
      ) : (
        <div className="flex xl:flex-row max-lg:flex-col gap-6 p-6 min-h-0 flex-1">
          <ScrollArea className="flex-1 min-h-0 min-w-50">
            <CheckoutCatalogs catalogs={installedCatalogs} isReadOnly={true} />
          </ScrollArea>
          <div className="min-w-0 shrink-0 lg:w-[550px] lg:sticky self-start">
            <DeploymentSummaryCard
              isPopover={false}
              deployment={deployment ?? null}
              itemCount={installedCatalogs.length}
              packageDescription={deployment?.description}
              isLoading={deploymentLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
}
